import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { formatCurrency, API_URL } from "../lib/utils";
import { printInvoice } from "../lib/invoicePrint";
import { SaftValidationCard } from "../components/fiscal/SaftValidationCard";
import { ManualRecoveryDialog } from "../components/fiscal/ManualRecoveryDialog";
import { FiscalDocumentsList } from "../components/fiscal/FiscalDocumentsList";
import { SaftPeriodsCard } from "../components/fiscal/SaftPeriodsCard";
import axios from "axios";
import { toast } from "sonner";
import { FileSpreadsheet, Building2, Calendar, RefreshCw, AlertCircle, FilePlus2, History } from "lucide-react";

const SAFTPage = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [exports, setExports] = useState([]);
  const [settings, setSettings] = useState(null);
  const [software, setSoftware] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const today = new Date();
  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);
  const opts = { withCredentials: true };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, st, sw, ex] = await Promise.all([
        axios.get(`${API_URL}/invoices?start_date=${startDate}&end_date=${endDate}`, opts),
        axios.get(`${API_URL}/settings`, opts),
        axios.get(`${API_URL}/fiscal/software`, opts),
        axios.get(`${API_URL}/saft/exports`, opts),
      ]);
      setInvoices(inv.data);
      setSettings(st.data);
      setSoftware(sw.data);
      setExports(ex.data);
    } catch {
      toast.error("Erro ao carregar dados fiscais");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); setValidation(null); }, [fetchData]);

  const handleValidate = async () => {
    setValidating(true);
    try {
      const r = await axios.get(`${API_URL}/saft/validate?start_date=${startDate}&end_date=${endDate}`, opts);
      setValidation(r.data);
      (r.data.xsd_valid ? toast.success : toast.error)(r.data.xsd_valid ? "SAF-T válido segundo o XSD oficial" : "SAF-T com erros de esquema");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao validar");
    } finally {
      setValidating(false);
    }
  };

  const handleExport = async () => {
    try {
      toast.info("A gerar ficheiro SAF-T (AO)…");
      const r = await axios.get(`${API_URL}/saft/export?start_date=${startDate}&end_date=${endDate}`, { ...opts, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || `SAFT_AO_${settings?.nif || ""}_${startDate}_${endDate}.xml`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      toast.success("Ficheiro SAF-T exportado — valide-o e entregue no portal da AGT");
      axios.get(`${API_URL}/saft/exports`, opts).then((x) => setExports(x.data)).catch(() => {});
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return null; } }) : e.response?.data?.detail;
      toast.error(detail || "Erro ao exportar SAF-T");
    }
  };

  const handleVerify = async (doc) => {
    try {
      const r = await axios.get(`${API_URL}/invoices/${doc.invoice_id}/verify`, opts);
      (r.data.valid ? toast.success : toast.error)(r.data.valid ? `Assinatura válida (${doc.invoice_number} · chave v${r.data.key_version})` : `Assinatura INVÁLIDA: ${r.data.reason || "documento alterado"}`);
    } catch {
      toast.error("Erro ao verificar assinatura");
    }
  };

  const totals = invoices.filter((i) => i.status !== "A" && i.status !== "cancelled").reduce((s, i) => ({ total: s.total + (i.total || 0), tax: s.tax + (i.tax || 0) }), { total: 0, tax: 0 });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="saft-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] flex items-center gap-3"><FileSpreadsheet className="w-8 h-8 text-primary" /> Fiscal · SAF-T (AO)</h1>
          <p className="text-muted-foreground">Documentos assinados, ficheiro SAF-T validado e recuperação de documentos manuais — Decreto Presidencial n.º 312/18</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setManualOpen(true)} className="gap-2" data-testid="saft-manual-recovery-btn"><FilePlus2 className="w-4 h-4" /> Documento manual</Button>
          <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Atualizar</Button>
        </div>
      </div>

      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <div className="p-3 rounded-lg bg-primary/10"><Building2 className="w-6 h-6 text-primary" /></div>
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-semibold">{settings?.restaurant_name || "Empresa"}</h3>
            <p className="text-sm text-muted-foreground">NIF {settings?.nif || "—"} · {settings?.address || "morada em falta"}{settings?.city ? `, ${settings.city}` : ""}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Validação AGT</p>
            <p className="font-bold font-mono" data-testid="saft-validation-number">n.º {software?.validation_number ?? "…"}</p>
            <Badge variant={software?.certified ? "default" : "secondary"}>{software?.certified ? "Validado" : "Pré-certificação"}</Badge>
          </div>
          {settings?.training_mode && <Badge className="bg-amber-500/20 text-amber-600">MODO FORMAÇÃO</Badge>}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-2"><Label className="flex items-center gap-2"><Calendar className="w-4 h-4" /> Início</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-44" data-testid="saft-start-date" /></div>
          <div className="space-y-2"><Label>Fim</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-44" data-testid="saft-end-date" /></div>
          <div className="flex-1 grid grid-cols-3 gap-2 text-right">
            <div><p className="text-xs text-muted-foreground">Documentos</p><p className="font-bold">{invoices.length}</p></div>
            <div><p className="text-xs text-muted-foreground">Volume</p><p className="font-bold font-mono">{formatCurrency(totals.total)}</p></div>
            <div><p className="text-xs text-muted-foreground">IVA</p><p className="font-bold font-mono">{formatCurrency(totals.tax)}</p></div>
          </div>
        </CardContent>
      </Card>

      <SaftValidationCard validation={validation} validating={validating} onValidate={handleValidate} onExport={handleExport} />

      <SaftPeriodsCard />

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Documentos do período</CardTitle>
          <CardDescription>FT, FR, NC — com estado, assinatura e 2.ª via</CardDescription>
        </CardHeader>
        <CardContent>
          <FiscalDocumentsList docs={invoices} onPrint={(d) => printInvoice(d, settings || {})} onVerify={handleVerify} />
        </CardContent>
      </Card>

      {exports.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><History className="w-5 h-5" /> Exportações SAF-T</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {exports.slice(0, 10).map((x) => (
              <div key={x.export_id} className="flex items-center justify-between text-sm p-2 rounded border" data-testid={`saft-export-${x.export_id}`}>
                <span className="font-mono">{x.filename || `${x.start_date} → ${x.end_date}`}</span>
                <span className="text-muted-foreground">{x.total_invoices ?? x.invoices ?? 0} doc(s) · {(x.created_at || "").slice(0, 16).replace("T", " ")}{x.xsd_valid === false ? " · INVÁLIDO" : ""}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4 flex gap-3 text-sm">
          <AlertCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Cada documento é assinado (RSA) e encadeado com o anterior da série — alterar ou apagar um documento é impossível; corrige-se com Nota de Crédito ou anulação.</li>
            <li>Os 4 caracteres da assinatura e a menção "Processado por programa validado n.º {software?.validation_number ?? "0"}/AGT" são impressos em todos os documentos.</li>
            <li>A comunicação à AGT faz-se com o ficheiro SAF-T (AO) do período, entregue no portal do contribuinte — não existe submissão factura a factura.</li>
            <li>Documentos anulados mantêm-se no ficheiro com estado "A"; consultas de mesa vão em WorkingDocuments; recibos em Payments.</li>
          </ul>
        </CardContent>
      </Card>

      <ManualRecoveryDialog open={manualOpen} onOpenChange={setManualOpen} onDone={fetchData} />
    </div>
  );
};

export default SAFTPage;
